import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

// antd
import { Form, Input, Typography } from 'antd'

// slices
import {
    clearPendingWidgetRedirect,
    isLoggedInLogin,
    loginForm,
    loginState,
    setEmailDisable,
} from './login.slice'

// assets
import logo from '../../assets/images/logo.png'

// components
import Button from '../../components/button/button'
import { EyeIcon, EyeSlashIcon } from '../../assets/icons/login/PasswordIcons'
import { showActionNotification } from '../../components/notification'

// routes
import {
    PRIVATE_ROUTES,
    PUBLIC_ROUTES,
    buildProjectTaskTabSearch,
} from '../../routes'

// helpers
import { getNavigateTargetFromWidgetRedirect } from '../../helpers/widgetRedirect'
import { setIsByPass } from '../workSpace/workSpace.slice'
import { FORM_VALIDATION_RULES } from '../../constants/validationRules/form'

// css
import './LoginForm.css'

function LoginForm() {
    const [form] = Form.useForm()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { pendingWidgetRedirect } = useSelector(loginState)
    const { Text } = Typography
    const [loadingButton, setLoadingButton] = useState(false)

    useEffect(() => {
        dispatch(setEmailDisable(false))
    }, [dispatch])

    const redirectAfterLogin = (profile) => {
        const widgetTarget = getNavigateTargetFromWidgetRedirect(
            pendingWidgetRedirect
        )
        if (widgetTarget) {
            navigate(widgetTarget, { replace: true })
            dispatch(clearPendingWidgetRedirect())
            return
        }

        if (profile?.isSupportUser) {
            if (profile?.isSuperAdmin) {
                navigate(PRIVATE_ROUTES.clients, { replace: true })
                return
            }
            const projectId = profile?.activeProjectId?._id
            if (projectId) {
                const isMemberUser = profile?.isExistingUser
                if (isMemberUser) {
                    navigate(
                        {
                            pathname: PRIVATE_ROUTES.projects.projectIdView(
                                projectId,
                                'board'
                            ),
                            search: buildProjectTaskTabSearch('development'),
                        },
                        { replace: true }
                    )
                } else {
                    navigate(PRIVATE_ROUTES.projects.projectId(projectId), {
                        replace: true,
                    })
                }
            }
            return
        }

        if (profile?.activeProject === null) {
            navigate(PRIVATE_ROUTES.projects.root, { replace: true })
        }
    }

    // ON LOGIN VIA EMAIL AND PASSWORD
    async function onLogin(value) {
        setLoadingButton(true)
        const result = await dispatch(loginForm(value))
        const data = result?.payload?.data
        if (data) {
            const { success, message: loginMessage } = data
            if (success) {
                const checkLogin = await dispatch(isLoggedInLogin())
                const checkSuccessLogin = checkLogin?.payload?.data
                if (checkSuccessLogin?.success) {
                    dispatch(setIsByPass(true))
                    showActionNotification({
                        type: 'success',
                        message: loginMessage,
                    })
                    localStorage.setItem(
                        'currentProject',
                        JSON.stringify({
                            id: checkSuccessLogin?.data?.activeProject?._id,
                            name: checkSuccessLogin?.data?.activeProject?.name,
                        })
                    )
                    redirectAfterLogin(checkSuccessLogin?.data)
                } else {
                    showActionNotification({
                        type: 'error',
                        message:
                            checkSuccessLogin?.message ||
                            'Something went wrong.',
                    })
                }
            } else {
                showActionNotification({
                    type: 'error',
                    message: loginMessage || 'Something went wrong.',
                })
            }
        }
        setLoadingButton(false)
    }

    // GOOGLE LOGIN SUCCESS HANDLER
    // const handleGoogleSuccess = async (credentialResponse) => {
    //     setLoadingButton(true)
    //     const result = await dispatch(
    //         loginGoogleSSO({
    //             id_token: credentialResponse.credential,
    //         })
    //     )
    //     const data = result?.payload?.data
    //     if (data?.success) {
    //         const checkLogin = await dispatch(isLoggedInLogin())
    //         const checkSuccessLogin = checkLogin?.payload?.data
    //         if (checkSuccessLogin?.success) {
    //             dispatch(setIsByPass(true))
    //             showActionNotification({
    //                 type: 'success',
    //                 message: data?.message || 'Login Successful',
    //             })
    //             localStorage.setItem(
    //                 'currentProject',
    //                 JSON.stringify({
    //                     id: checkSuccessLogin?.data?.activeProject?._id,
    //                     name: checkSuccessLogin?.data?.activeProject?.name,
    //                 })
    //             )
    //             redirectAfterLogin(checkSuccessLogin?.data)
    //         } else {
    //             showActionNotification({
    //                 type: 'error',
    //                 message:
    //                     checkSuccessLogin?.message || 'Something went wrong.',
    //             })
    //         }
    //     } else {
    //         showActionNotification({
    //             type: 'error',
    //             message: data?.message || 'Google Login Failed',
    //         })
    //     }
    //     setLoadingButton(false)
    // }

    return (
        <div className="glass-card">
            <div className="login-logo-container">
                <img src={logo} alt="logo" className="login-form-logo-img" />
            </div>

            <h2 className="welcome-title">Welcome, Let's get to work!</h2>
            <p className="welcome-subtitle">Sign in to your account</p>

            <Form
                layout="vertical"
                name="loginForm"
                onFinish={onLogin}
                requiredMark={false}
                form={form}
                autoComplete="off"
                className="custom-login-form"
                style={{
                    rowGap: '20px',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Form.Item
                    name="email"
                    rules={FORM_VALIDATION_RULES.email.rules}
                >
                    <Input
                        className="custom-input"
                        placeholder="pmx@gmail.com"
                        autoComplete="off"
                    />
                </Form.Item>

                <Form.Item
                    name="password"
                    rules={[
                        FORM_VALIDATION_RULES.password.required,
                        FORM_VALIDATION_RULES.password.minLength8,
                        FORM_VALIDATION_RULES.password.includeUpperLowerNumber,
                    ]}
                >
                    <Input.Password
                        className="custom-input"
                        placeholder="Enter your Password"
                        autoComplete="new-password"
                        iconRender={(visible) =>
                            visible ? (
                                <div
                                    onClick={() => {}}
                                    style={{
                                        cursor: 'pointer',
                                    }}
                                >
                                    <EyeIcon />
                                </div>
                            ) : (
                                <div
                                    onClick={() => {}}
                                    style={{
                                        cursor: 'pointer',
                                    }}
                                >
                                    <EyeSlashIcon />
                                </div>
                            )
                        }
                    />
                </Form.Item>

                <div style={{ textAlign: 'right' }}>
                    <Text
                        style={{
                            color: 'rgba(255, 255, 255, 0.7)',
                            cursor: 'pointer',
                        }}
                        onClick={() => navigate(PUBLIC_ROUTES.forgotPassword)}
                    >
                        Forgot password?
                    </Text>
                </div>

                <Button
                    props={{
                        text: 'Login',
                        htmlType: 'submit',
                        className: 'login-submit-btn',
                        disabled: loadingButton,
                        loadingButton: loadingButton,
                    }}
                />

                {/* <div className="login-divider">
                    <span
                        style={{
                            color: '#ffffff',
                        }}
                    >
                        OR
                    </span>
                </div>

                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: '100%',
                    }}
                >
                    <GoogleLogin
                        onSuccess={handleGoogleSuccess}
                        onError={() => {
                            message.error('Google Login Failed')
                        }}
                        theme="filled_black"
                        shape="pill"
                        text="continue_with"
                        width="100%"
                    />
                </div> */}
            </Form>
        </div>
    )
}

export default LoginForm
